import React, { useEffect, useRef } from 'react';
import { Animated, Dimensions, Image, Pressable, StyleSheet, View } from 'react-native';
import { getNotificationBannerStyle } from './variants';
import { BMPrimitives, useBMTheme } from '../../theme';
import BMText from '../Text/BMText';
import BMButtonIcon from '../ButtonIcon/BMButtonIcon';
import IcSystemCrossClose from '@traveloka/district-icon/IcSystemCrossClose';
import NotificationBanner from '../NotificationBannerContainer';
const BMNotificationBanner = ({ type, title, message, width, ctaLabel, duration = 10, transitionDuration, illustration, slideEnterAnimationPosition = 'RIGHT', slideExitAnimationPosition = 'RIGHT', enterAnimationType = 'SLIDE', exitAnimationType = 'SLIDE', translateX, translateY, Icon, withCloseButton, testID, }) => {
    const { BMComponents, BMElevation } = useBMTheme();
    const notificationStyle = getNotificationBannerStyle(type, BMComponents);
    const screenWidth = Dimensions.get('window').width;
    const screenHeight = Dimensions.get('window').height;
    // close animation value
    const translateXClose = useRef(0);
    const translateYClose = useRef(0);
    const getTranslateXPosition = () => {
        if (slideExitAnimationPosition === 'LEFT') {
            translateXClose.current = -screenWidth;
        }
        else if (slideExitAnimationPosition === 'RIGHT') {
            translateXClose.current = screenWidth + 200;
        }
        if (enterAnimationType === 'SLIDE') {
            switch (slideEnterAnimationPosition) {
                case 'LEFT':
                    return -screenWidth;
                case 'RIGHT':
                    return screenWidth + 200;
                default:
                    return 0;
            }
        }
        else {
            return 0;
        }
    };
    const getTranslateYPosition = () => {
        if (slideExitAnimationPosition === 'TOP') {
            translateYClose.current = -screenHeight;
        }
        else if (slideExitAnimationPosition === 'BOTTOM') {
            translateYClose.current = screenHeight + 200;
        }
        if (enterAnimationType === 'SLIDE') {
            switch (slideEnterAnimationPosition) {
                case 'TOP':
                    return -screenHeight;
                case 'BOTTOM':
                    return screenHeight + 200;
                default:
                    return 0;
            }
        }
        else {
            return 0;
        }
    };
    const translateXOpen = useRef(new Animated.Value(translateX || getTranslateXPosition())).current;
    const translateYOpen = useRef(new Animated.Value(translateY || getTranslateYPosition())).current;
    const opacityAnim = useRef(new Animated.Value(enterAnimationType === 'FADE' ? 0 : 1)).current;
    const openAnimation = () => {
        if (enterAnimationType === 'SLIDE') {
            if (slideEnterAnimationPosition === 'LEFT' || slideEnterAnimationPosition === 'RIGHT') {
                Animated.timing(translateXOpen, {
                    toValue: 0,
                    duration: Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended),
                    useNativeDriver: false,
                }).start();
            }
            else {
                Animated.timing(translateYOpen, {
                    toValue: 0,
                    duration: Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended),
                    useNativeDriver: false,
                }).start();
            }
        }
        else {
            Animated.timing(opacityAnim, {
                toValue: 1,
                duration: Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended),
                useNativeDriver: false,
            }).start();
        }
    };
    const closeAnimation = () => {
        if (exitAnimationType === 'SLIDE') {
            if (slideExitAnimationPosition === 'LEFT' || slideExitAnimationPosition === 'RIGHT') {
                Animated.timing(translateXOpen, {
                    toValue: translateXClose.current,
                    duration: Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended),
                    useNativeDriver: false,
                }).start();
            }
            else {
                Animated.timing(translateYOpen, {
                    toValue: translateYClose.current,
                    duration: Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended),
                    useNativeDriver: false,
                }).start();
            }
        }
        else {
            Animated.timing(opacityAnim, {
                toValue: 0,
                duration: Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended),
                useNativeDriver: false,
            }).start();
        }
    };
    useEffect(() => {
        openAnimation();
        if (!withCloseButton) {
            setTimeout(handleClose, duration * 1000);
        }
    }, []);
    const handleClose = () => {
        closeAnimation();
        setTimeout(() => {
            var _a;
            if (NotificationBanner === null || NotificationBanner === void 0 ? void 0 : NotificationBanner.instance)
                (_a = NotificationBanner === null || NotificationBanner === void 0 ? void 0 : NotificationBanner.close) === null || _a === void 0 ? void 0 : _a.call(NotificationBanner);
        }, Number(transitionDuration || BMPrimitives.BMAnimation.duration.extended));
    };
    const generateCTALabels = () => {
        const ctaLabelComponents = [];
        const CtaLabelComponent = ({ ctaLabel, style }) => (<Pressable onPress={() => {
                ctaLabel.onAction();
                handleClose();
            }} style={[styles.actionButton, style]}>
        <BMText numberOfLines={1} style={[
                {
                    color: notificationStyle.messageColor,
                    ...BMPrimitives.BMTypography.uiNormalEm,
                    marginTop: message ? BMPrimitives.BMSize.xs : 0,
                },
            ]}>
          {ctaLabel.label}
        </BMText>
      </Pressable>);
        if (ctaLabel) {
            if (Array.isArray(ctaLabel)) {
                // Generate all cta label. Currently cap at 2 labels
                for (let i = 0; i < 2; i++) {
                    let labelStyle;
                    if (i === 0) {
                        labelStyle = {
                            maxWidth: '50%',
                        };
                    }
                    else {
                        labelStyle = {
                            marginLeft: BMPrimitives.BMSize.sm,
                            flexShrink: 1,
                        };
                    }
                    ctaLabelComponents.push(<CtaLabelComponent ctaLabel={ctaLabel[i]} key={i} style={labelStyle}/>);
                }
            }
            else {
                ctaLabelComponents.push(<CtaLabelComponent ctaLabel={ctaLabel} key={0}/>);
            }
        }
        return ctaLabelComponents;
    };
    return (<View>
      <Animated.View style={[
            styles.container,
            {
                backgroundColor: notificationStyle.backgroundColor,
                width: width || 336,
                transform: [
                    {
                        translateY: translateYOpen !== null && translateYOpen !== void 0 ? translateYOpen : 0,
                    },
                    {
                        translateX: translateXOpen !== null && translateXOpen !== void 0 ? translateXOpen : 0,
                    },
                ],
                opacity: enterAnimationType == 'FADE' || exitAnimationType == 'FADE' ? opacityAnim : 1,
                ...BMElevation.elevation2,
            },
        ]} testID={testID}>
        {(illustration || Icon) && (<View style={styles.imageContainer}>
            {illustration && (<Image source={{
                    uri: illustration,
                }} style={[{ borderRadius: BMPrimitives.BMRadius.rounded }, styles.image]}/>)}
            {Icon && !illustration && (<View style={{ marginTop: illustration ? BMPrimitives.BMSize.md : 0 }}>
                <Icon accentColor={notificationStyle.titleColor} width={24} height={24}/>
              </View>)}
          </View>)}
        <View style={[
            styles.flex,
            {
                marginLeft: illustration || Icon ? BMPrimitives.BMSize.md : 0,
            },
        ]}>
          <BMText numberOfLines={1} style={{ ...BMPrimitives.BMTypography.titleSmall, color: notificationStyle.titleColor }}>
            {title}
          </BMText>
          <BMText style={{
            ...BMPrimitives.BMTypography.paragraphSmall,
            color: notificationStyle.messageColor,
            marginTop: title ? BMPrimitives.BMSize.xs : 0,
        }} numberOfLines={3}>
            {message}
          </BMText>
          {ctaLabel && <View style={styles.ctaLabelsWrapper}>{generateCTALabels()}</View>}
        </View>
        {withCloseButton ? (<View style={{ marginLeft: BMPrimitives.BMSize.md }}>
            <BMButtonIcon Icon={IcSystemCrossClose} timerDuration={duration !== null && duration !== void 0 ? duration : 10} onPress={handleClose} size="INSET"/>
          </View>) : null}
      </Animated.View>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        display: 'flex',
        flexDirection: 'row',
        padding: BMPrimitives.BMSize.md,
        borderRadius: BMPrimitives.BMRadius.md,
        maxWidth: '100%',
    },
    ctaLabelsWrapper: {
        display: 'flex',
        flexDirection: 'row',
    },
    imageContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    image: {
        width: 56,
        height: 56,
    },
    flex: {
        flex: 1,
    },
    actionButton: { alignSelf: 'flex-start', maxWidth: '100%' },
    wrapper: {
        position: 'absolute',
    },
});
export default BMNotificationBanner;
